import React, { useEffect } from "react";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";
import { AlertMessagePermission, AlertMessages, CustomMessageProvider } from "go-alert/AlertMessage";
import { AppErrorBoundary } from "go-component/components/AppErrorBoundary";
import ScrollToTop from "go-core/components/ScrollToTop";
import { useCrashReporter } from "go-core/hooks";
import { useMomentLocaleForUser } from "go-core/hooks/useMomentLocale";
import { ConfirmationServiceProvider } from "go-form/components/ModalConfirm";
import Routes from "./Routes";
import GoogleAnalytics from "./components/Helmet/GoogleAnalytics";
import { IntercomApp } from "./services/Intercom/intercomApp";
import { AuthCredentials, AuthCredentialsService } from "./services/Security/AuthCredentialsService";
import { restApi } from "./services/Security/api";
import { refreshTokenApi } from "./services/Security/applyAppTokenRefreshInterceptor";
import { securityApi } from "./services/Security/auth";
import { selectUser } from "./services/users/selectors";
import { getApiURL, getCrashReportApiToken, getTokenClientId, getTokenClientSecret } from "./utils/envutil";

const url = getApiURL();

AuthCredentialsService.init({
	clientId: getTokenClientId(),
	secretId: getTokenClientSecret(),
	url,
} as AuthCredentials);
restApi.defaults.baseURL = `${url}/ajax/`;
securityApi.defaults.baseURL = `${url}/`;
refreshTokenApi.defaults.baseURL = `${url}/`;

function App() {
	const user = useSelector(selectUser);
	const { t } = useTranslation();
	const intercomId = IntercomApp.getIntercomId(user);
	const permissions: AlertMessagePermission[] = [
		{
			id: "DEFAULT",
			title: t("enums.users.permissions.DEFAULT"),
		},
		{
			id: "USERS_MANAGMENT",
			title: t("enums.users.permissions.USERS_MANAGMENT"),
		},
	];

	useMomentLocaleForUser(user.organizations);

	useEffect(() => {
		if (i18n.language) document.documentElement.lang = i18n.language;
	}, [i18n.language]);

	useCrashReporter("GoAccounts Front", url, getCrashReportApiToken());

	return (
		<div className="App">
			<GoogleAnalytics />
			<IntercomProvider autoBoot={false} appId={intercomId ? intercomId : ""}>
				<BrowserRouter>
					<AppErrorBoundary>
						<CustomMessageProvider>
							<ConfirmationServiceProvider>
								<AlertMessages permissions={permissions} />
								<ScrollToTop />
								<Routes />
							</ConfirmationServiceProvider>
						</CustomMessageProvider>
					</AppErrorBoundary>
				</BrowserRouter>
			</IntercomProvider>
		</div>
	);
}

export default App;
